import * as React from "react";

import { useTranslation } from "react-i18next";

import { CheckIcon } from "lucide-react";

import { useLocations, type Location } from "@/api/locations";
import { useEntitySelection } from "@/components/entity-select/useEntitySelection";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";

import { Pill } from "./Pill";

export type InPillProps = {
  value: number[];
  onChange: (next: number[]) => void;
};

export function InPill({ value, onChange }: InPillProps) {
  const { t } = useTranslation();
  const [query, setQuery] = React.useState("");

  const sel = useEntitySelection<Location>({
    value,
    query,
    useSearchResults: (q) => {
      const { data, isLoading } = useLocations({ search: q || undefined });
      return { data, isLoading };
    },
    useResolveSelected: (ids) => {
      const { data } = useLocations({});
      return { data: data?.filter((l) => ids.includes(l.id)) };
    },
    getId: (l) => l.id,
  });

  const isDefault = value.length === 0;
  const label = isDefault
    ? t("demand.pills.in.allLocations")
    : value.length === 1
      ? (sel.selectedEntities[0]?.name ??
        t("demand.pills.in.location", { count: 1 }))
      : t("demand.pills.in.location", { count: value.length });

  function toggle(id: number) {
    onChange(
      value.includes(id) ? value.filter((v) => v !== id) : [...value, id],
    );
  }

  return (
    <Pill
      label={label}
      isDefault={isDefault}
      onClear={isDefault ? undefined : () => onChange([])}
    >
      <Command shouldFilter={false}>
        <CommandInput
          placeholder={t("demand.pills.in.searchPlaceholder")}
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          {sel.isLoading && (
            <div className="text-muted-foreground p-2 text-sm">
              {t("common.loading")}
            </div>
          )}
          <CommandEmpty>{t("demand.pills.noMatches")}</CommandEmpty>
          {sel.selectedEntities.length > 0 && (
            <>
              <CommandGroup
                heading={t("components.entitySelect.selectedHeading")}
              >
                {sel.selectedEntities.map((l) => (
                  <CommandItem
                    key={`sel-${l.id}`}
                    value={`sel-${l.name}-${l.id}`}
                    onSelect={() => toggle(l.id)}
                  >
                    {l.name}
                    <CheckIcon className="ml-auto size-4 opacity-100" />
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
            </>
          )}
          <CommandGroup>
            {sel.results.map((l) => (
              <CommandItem
                key={l.id}
                value={`${l.name}-${l.id}`}
                onSelect={() => toggle(l.id)}
              >
                {l.name}
                <CheckIcon className="ml-auto size-4 opacity-0" />
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </Pill>
  );
}
